/*
 Dexie-backed in-memory emoji cache

 Purpose:
 - Mirror the `emojis` table into fast in-memory maps keyed by id, shortcode, and unified.
 - Offer synchronous lookup helpers for UI features (emoji search, TipTap emoji extension, editors, previews).

 How it works:
 - `ensureEmojiCache()` primes the cache from Dexie and subscribes via `liveQuery` to keep it up to date.
 - `getEmojiById`, `getEmojiByShortcode`, `getEmojiByUnified`, and `getAllEmojis` read from the in-memory maps.
*/
import { liveQuery } from 'dexie';

import db from './db';
import { logError } from './logger';
import { currentWorkspaceId } from './meta';
import { subscribeWorkspaceIdScope } from './workspaceIdScope';

let initialized = false;
let byId = new Map();
let byShortcode = new Map();
let byUnified = new Map();
let lastRows = [];

function fill(emojis) {
  const idMap = new Map();
  const scMap = new Map();
  const uniMap = new Map();

  // Custom emojis are cached across every workspace the user belongs to (the
  // server only syncs those), so the picker/search can offer them in any chat.
  for (const e of emojis || []) {
    if (!e) continue;

    // track all emojis by id
    if (e.id) idMap.set(e.id, e);

    // Emojis from chats the user isn't in ride along purely so existing messages can
    // resolve them — by id only. Keeping them out of the shortcode and unified maps is
    // what stops autocomplete and :shortcode: expansion from offering them.
    if (e.resolve_only) continue;

    // only track active emojis by shortcode and unified
    if (e.sort_order >= 0) {
      // On cross-workspace shortcode collisions the current chat's emoji wins;
      // otherwise first-seen wins (rows arrive in stable primary-key order).
      if (e.shortcode) {
        const existing = scMap.get(e.shortcode);
        const existingWins = existing && (existing.workspace_id === currentWorkspaceId() || e.workspace_id !== currentWorkspaceId());
        if (!existingWins) scMap.set(e.shortcode, e);
      }
      if (e.unified) uniMap.set(e.unified.toUpperCase().trim(), e);
    }
  }

  byId = idMap;
  byShortcode = scMap;
  byUnified = uniMap;
}

export function ensureEmojiCache() {
  if (initialized) return;
  initialized = true;

  db.emojis
    .toArray()
    .then((rows) => {
      lastRows = rows;
      fill(rows);
    })
    .catch((err) => logError('emojiCache', 'Failed to prime emoji cache', err));

  subscribeWorkspaceIdScope(() => fill(lastRows));

  liveQuery(() => db.emojis.toArray()).subscribe({
    next: (rows) => {
      lastRows = rows;
      fill(rows);
    },
    error: (err) => logError('emojiCache', 'liveQuery error', err),
  });
}

export function getEmojiById(id) {
  return byId.get(id);
}

export function getEmojiByShortcode(shortcode) {
  return byShortcode.get(shortcode);
}

export function getEmojiByUnified(unified) {
  return byUnified.get(unified?.toUpperCase().trim());
}

// Every caller of this is an offer surface (search, suggestions, the picker's fallback),
// so resolve-only emojis are filtered out — they belong to chats the user isn't in and
// aren't theirs to send. Rendering an emoji a message already references goes through
// getEmojiById, which still sees everything.
export function getAllEmojis() {
  return Array.from(byId.values()).filter((e) => !e.resolve_only);
}
